import Glide from "@glidejs/glide";
import { monitorForChanges } from "../helpers/_observer-handler";
import { isMobile } from "../../common/_constants";
import handleMediaQueryChange from "../helpers/_media_query";

const glideInstances = new WeakMap();

export const glideConfig = (element, config) => {
    const existing = glideInstances.get(element);
    if (existing) {
        existing.destroy();
    }

    const glide = new Glide(element, config);
    glide.mount();
    glideInstances.set(element, glide);
};

export const glideSliderHandler = () => {
    const slidesContainer = document.querySelector(".glide__slides");
    const activeSlideImg = document.querySelector(
        ".glide__slide.glide__slide--active img",
    );
    const slideControls = document.querySelectorAll(
        ".glide button.slider__arrow",
    );

    monitorForChanges(".glide__slides", () => {
        const slideContent = document.querySelector(
            ".glide__slide.glide__slide--active .slide-gallery-content",
        );
        if (slideContent) {
            for (const slideControl of slideControls) {
                slideControl.style.top = `calc(50% - ${slideContent.clientHeight / 2}px)`;
            }
        }
    });

    monitorForChanges(".glide__slide.glide__slide--active", () => {
        const slideContent = document.querySelector(
            ".glide__slide.glide__slide--active .slide-gallery-content",
        );
        if (slideContent) {
            slidesContainer.style.height = `${activeSlideImg.clientHeight + slideContent.clientHeight}px`;
        }
    });
};

export const glideCarouselConfig = (config, indexThreshold) => {
    const glide = new Glide(".glide", config);
    const slides = document.querySelectorAll(".glide ul li");

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const mutator = function (Glide, Components, Events) {
        return {
            modify(translate) {
                if (indexThreshold) {
                    if (Glide.index === 0) {
                        return translate;
                    } else if (Glide.index === Components.Sizes.length - 1) {
                        return (
                            translate -
                            (Components.Sizes.width -
                                Components.Sizes.slideWidth)
                        );
                    } else {
                        return (
                            translate -
                            (Components.Sizes.width -
                                Components.Sizes.slideWidth) /
                                indexThreshold
                        );
                    }
                } else {
                    return translate;
                }
            },
        };
    };

    glide.mutate([mutator]).mount();

    if (slides.length === 1) {
        for (const slide of slides) {
            slide.style.width = "100%";
            slide.parentElement.style.width = "100%";
        }
    }
};

export function glideCarouselInfiniteBulletConfig(rootEl, glideOptions) {
    const glide = new Glide(rootEl, glideOptions);
    const bulletsContainer = rootEl.querySelector(".glide__bullets");

    const bullets = bulletsContainer.querySelectorAll(".glide__bullet");

    function updateBullets() {
        const total = bullets.length;
        const realIndex = ((glide.index % total) + total) % total;

        bullets.forEach((bullet, i) => {
            bullet.classList.toggle("glide__bullet--active", i === realIndex);
        });
    }

    // Remove Glide's auto-added active class on mount
    glide.on("mount.after", () => {
        bullets.forEach((b) => b.classList.remove("glide__bullet--active"));
        updateBullets();
    });

    glide.on("move.after", updateBullets);

    // Manually hook bullet click behavior
    bullets.forEach((bullet, i) => {
        bullet.addEventListener("click", () => glide.go(`=${i}`));
    });

    glide.mount();
}

export const partnerContentSlider = () => {
    const slideRelatedNewsEnabled = document.querySelector(
        "[data-enable-slider='true']",
    );
    const partnerContent = document.querySelector(
        "[data-plugin-type='partner-content']",
    );
    if (slideRelatedNewsEnabled && partnerContent) {
        const slides = partnerContent.querySelectorAll(
            "[data-enable-slider='true'] .glide__slide",
        );
        const slideCount = slides.length;
        const slideControler = partnerContent.querySelector(
            "[data-glide-el='controls']",
        );

        if (isMobile()) {
            glideCarouselConfig(
                {
                    type: "slider",
                    perView: slideCount >= 2 ? 1.5 : 1,
                    focusAt: 0,
                    bound: slideCount >= 2,
                    gap: 10,
                    rewind: false,
                },
                2,
            );

            if (slideCount < 2) {
                slideControler.classList.add("hidden");
            } else {
                slideControler.classList.remove("hidden");
            }
        } else {
            glideCarouselConfig({
                type: "slider",
                focusAt: 0,
                startAt: 0,
                perView: 4,
                bound: slideCount >= 4,
                swipeThreshold: slideCount >= 4,
                dragThreshold: slideCount >= 4,
                keyboard: slideCount >= 4,
                rewind: false,
                gap: 12,
            });

            if (slideCount < 4) {
                slideControler.classList.add("hidden");
            } else {
                slideControler.classList.remove("hidden");
            }
        }
    }
};

export const relatedNewsSlider = () => {
    if (isMobile()) {
        glideCarouselConfig(
            {
                type: "slider",
                perView: 1.25,
                focusAt: 0,
                gap: 20,
                bound: true,
                rewind: false,
            },
            2,
        );
    } else {
        glideConfig(".glid", {
            type: "slider",
            startAt: 0,
            perView: 3,
            swipeThreshold: false,
            dragThreshold: false,
            keyboard: false,
            gap: 35,
        });
    }
};

export const gallerySlider = () => {
    glideConfig(".glid", {
        type: "slider",
        touchAngle: 45,
        focusAt: 0,
        startAt: 0,
        keyboard: true,
        gap: 0,
        rewind: false,
    });
    glideSliderHandler();
};

export const imageLinksSlider = () => {
    const imageLinksPlugins = document.querySelectorAll(
        "[data-image-links-plugin] .glide",
    );

    if (imageLinksPlugins.length === 0) return;

    for (const plugin of imageLinksPlugins) {
        const slidesCount = plugin.querySelectorAll(".glide__slide").length;
        const desktopPerView = 4.26;
        const mobilePerView = 1.45;

        glideConfig(plugin, {
            type: slidesCount <= desktopPerView ? "slider" : "carousel",
            perView: desktopPerView,
            focusAt: 0.05,
            gap: 16,
            breakpoints: {
                768: {
                    type: slidesCount <= mobilePerView ? "slider" : "carousel",
                    perView: mobilePerView,
                    focusAt: slidesCount <= mobilePerView ? 0 : "center",
                },
            },
        });
    }
};

const snNowMobile = () => {
    const snNowPlugins = document.querySelectorAll("[data-sn-now] .glide");

    if (snNowPlugins.length === 0) return;

    snNowPlugins.forEach((plugin) => {
        glideConfig(plugin, {
            type: "slider",
            focusAt: 0,
            startAt: 0,
            perView: 2,
            keyboard: true,
            rewind: false,
            peek: 0,
            gap: 12,
            bound: true,
        });
    });
};

export const snNowSlider = () => {
    handleMediaQueryChange(snNowMobile, null);
};

const snShowcaseDesktop = () => {
    const snShowcasePlugins = document.querySelectorAll(
        "[data-sn-showcase] .glide",
    );

    if (snShowcasePlugins.length === 0) return;

    snShowcasePlugins.forEach((plugin) => {
        glideCarouselInfiniteBulletConfig(plugin, {
            type: "carousel",
            dragThreshold: false,
        });
    });
};

const snShowcaseMobile = () => {
    const snShowcasePlugins = document.querySelectorAll(
        "[data-sn-showcase] .glide",
    );

    if (snShowcasePlugins.length === 0) return;

    snShowcasePlugins.forEach((plugin) => {
        glideCarouselInfiniteBulletConfig(plugin, {
            type: "carousel",
            dragThreshold: true,
        });
    });
};

export const snShowcaseSlider = () => {
    handleMediaQueryChange(snShowcaseMobile, snShowcaseDesktop);
};

const topicSliderDesktop = () => {
    const topicPlugin = document.querySelectorAll("[data-topic-plugin] .glide");

    if (topicPlugin.length === 0) return;

    for (const plugin of topicPlugin) {
        const desktopPerView = 3;

        glideConfig(plugin, {
            type: "carousel",
            perView: desktopPerView,
            gap: 16,
            dragThreshold: false,
            swipeThreshold: false,
        });
    }
};

const topicSliderMobile = () => {
    const topicPlugin = document.querySelectorAll("[data-topic-plugin] .glide");

    if (topicPlugin.length === 0) return;

    for (const plugin of topicPlugin) {
        const slidesCount = plugin.querySelectorAll(".glide__slide").length;
        const mobilePerView = 1;

        glideConfig(plugin, {
            type: "carousel",
            perView: mobilePerView,
            focusAt: slidesCount <= mobilePerView ? 0 : "center",
            swipeThreshold: true,
            dragThreshold: true,
        });
    }
};

export const topicSlider = () => {
    handleMediaQueryChange(topicSliderMobile, topicSliderDesktop);
};
